/** @jsxImportSource @emotion/react */
"use client";
import type { Interpolation } from "@emotion/react";
import { useTranslations } from "@i18n/next";
import { HallowAnalyticsEvent, useAnalytics } from "@packages/analytics";
import { useParams } from "@packages/framework";
import type { BibleBookId } from "@packages/sdk";
import { useRequestBibleSaveMutations } from "@packages/sdk";
import type { ThemeType } from "@packages/ui";
import {
  Blockquote,
  Button,
  constantSemanticColors,
  ContentEditableDiv,
  createModal,
  cssMediaQueryMinWidthTablet,
  Form,
  FormField,
  FullscreenModalModal,
  getPlainTextFromContentEditable,
  ModalHeader,
  numericViewportHeight,
  spacing,
  standardSchemaResolver,
  Text,
  useForm,
  useModal,
} from "@packages/ui";
import type { ComponentProps } from "react";
import { useRef, useState } from "react";
import { z } from "zod";

import { bibleProperties } from "./analytics.utilities";
import { Highlights } from "./Highlights";
import { HighlightColors } from "./select.utilities";

const highlight: Interpolation<{ highlightId: number }> = ({
  highlightId,
}) => ({
  verticalAlign: "middle",
  backgroundColor: constantSemanticColors[`userHighlight${highlightId}`],
});

const editableDiv: Interpolation = {
  marginBlock: spacing.l,
};

const buttonPlacer: Interpolation = {
  position: "absolute",
  top: spacing.none,
  right: spacing.none,
  [cssMediaQueryMinWidthTablet]: {
    top: spacing.m,
    right: spacing.m,
  },
};

const header: Interpolation = {
  paddingBlock: spacing.none,
};

const modalStyle: Interpolation = {
  maxHeight: numericViewportHeight[100],
  maxWidth: "max-content",
};

const placeholderText: Interpolation<ThemeType> = (theme) => ({
  color: theme.semanticColors.neutralsMedium,
});

const NoteForm = ({
  isLoading,
  ...props
}: Omit<ComponentProps<typeof Form<{ note: string }>>, "children"> & {
  isLoading: boolean;
}) => {
  const t = useTranslations();
  const focusedOnce = useRef<boolean>(false);

  return (
    <Form {...props}>
      <FormField
        render={({ field: { value, ref, ...field } }) => (
          <ContentEditableDiv
            {...field}
            ref={(el) => {
              ref(el);
              if (el && !focusedOnce.current) {
                el.focus();
                focusedOnce.current = true;
              }
            }}
            type="body"
            size="l"
            placeholder={t("bible_add_a_note_placeholder")}
            placeholderCSS={placeholderText}
            css={editableDiv}
            disabled={isLoading}
            onFocus={(event) => {
              const range = document.createRange();
              range.selectNodeContents(event.currentTarget);
              range.collapse(false);
              const selection = window.getSelection();
              selection?.removeAllRanges();
              selection?.addRange(range);
            }}
            onChange={(event) =>
              field.onChange?.(
                getPlainTextFromContentEditable(event.currentTarget),
              )
            }
          >
            {(value ?? "").toString()}
          </ContentEditableDiv>
        )}
        name={"note"}
      />
      <Button
        type="submit"
        isLoading={isLoading}
        variant="outline"
        css={buttonPlacer}
        onMouseDown={(e) => e.preventDefault()}
      >
        {t("button_save")}
      </Button>
    </Form>
  );
};

type NewBibleNoteProps = {
  book: BibleBookId;
  chapter: number;
  color?: HighlightColors;
  end: number;
  start: number;
  text: string;
  title: string;
  translationId: number;
};

type BibleNoteModalProps = Pick<
  NewBibleNoteProps,
  "text" | "title" | "start" | "end" | "translationId" | "book" | "chapter"
> & {
  color: HighlightColors;
  notationId: number;
  note: string;
  screenName: string;
  translationName: string;
};

export const BibleNoteModal = {
  Create: createModal<NewBibleNoteProps>(
    ({
      text,
      title,
      chapter,
      start,
      end,
      book,
      translationId,
      color = HighlightColors.Yellow,
    }) => {
      const { translation: translationName } = useParams();
      const modal = useModal();
      const { addNotation } = useRequestBibleSaveMutations();
      const t = useTranslations();
      const form = useForm<{ note: string }>({
        defaultValues: { note: "" },
        resolver: standardSchemaResolver(
          z.object({ note: z.string().min(1, "error_required") }),
        ),
      });
      const { track } = useAnalytics();

      const onSubmit = async (data: { note: string }) => {
        await addNotation.mutateAsync({
          body: {
            book_id: book,
            chapter,
            color,
            note: data.note,
            selected_text: text,
            title,
            translation_id: translationId,
            verse_end: end,
            verse_start: start,
          },
        });
        track({
          event: HallowAnalyticsEvent.NoteAdded,
          properties: bibleProperties({
            book_id: book,
            chapter,
            translation_name: translationName,
            translation_id: translationId,
            verse_end: end,
            verse_start: start,
            type: "notation",
            screen_name: "bible",
          }),
        });
        modal.resolve();
        modal.remove();
      };

      return (
        <FullscreenModalModal
          open={modal.visible}
          onOpenChange={(open) => {
            if (open) return modal.show();
            modal.resolve();
            modal.remove();
          }}
          modalProps={{ contentCSS: modalStyle }}
          css={modalStyle}
        >
          <ModalHeader css={header}>
            {t("sharing_add_a_note_title")}
          </ModalHeader>
          <Blockquote>
            <Text lineClamp={5} type="bible">
              <span css={highlight({ highlightId: color })}>{text}</span>
            </Text>
            <Text type="title">{title}</Text>
          </Blockquote>
          <NoteForm
            {...form}
            formProps={{ onSubmit: form.handleSubmit(onSubmit) }}
            isLoading={addNotation.isPending}
          />
        </FullscreenModalModal>
      );
    },
  ),
  Edit: createModal<BibleNoteModalProps>(
    ({
      notationId,
      note,
      color,
      text,
      title,
      end,
      start,
      translationId,
      screenName,
      translationName,
      book,
      chapter,
    }) => {
      const modal = useModal();
      const { editNotation } = useRequestBibleSaveMutations();
      const form = useForm<{ note: string }>({
        defaultValues: { note },
        resolver: standardSchemaResolver(z.object({ note: z.string() })),
      });
      const t = useTranslations();
      const [highlightColor, setHighlightColor] =
        useState<HighlightColors>(color);
      const { track } = useAnalytics();

      const onSubmit = async (data: { note: string }) => {
        await editNotation.mutateAsync({
          params: {
            path: { id: notationId },
          },
          body: {
            color: highlightColor,
            note: data.note,
            selected_text: text,
            title,
            verse_end: end,
            verse_start: start,
          },
        });
        if (!note?.length && data.note.length) {
          track({
            event: HallowAnalyticsEvent.NoteAdded,
            properties: bibleProperties({
              book_id: book,
              chapter,
              color: highlightColor,
              screen_name: screenName,
              translation_id: translationId,
              translation_name: translationName,
              type: "notation",
              verse_end: end,
              verse_start: start,
            }),
          });
        } else {
          track({
            event: HallowAnalyticsEvent.EditedNote,
            properties: bibleProperties({
              book_id: book,
              chapter,
              color: highlightColor,
              screen_name: screenName,
              translation_id: translationId,
              translation_name: translationName,
              type: "notation",
              verse_end: end,
              verse_start: start,
            }),
          });
        }
        modal.resolve();
        modal.remove();
      };
      return (
        <FullscreenModalModal
          open={modal.visible}
          onOpenChange={(open) => {
            if (open) return modal.show();
            modal.resolve();
            modal.remove();
          }}
        >
          <ModalHeader css={header}>{t("bible_note_header")}</ModalHeader>
          <Blockquote>
            <Text lineClamp={5} type="bible">
              <span css={highlight({ highlightId: highlightColor })}>
                {text}
              </span>
            </Text>
            <Text type="title">{title}</Text>
          </Blockquote>
          <NoteForm
            formProps={{ onSubmit: form.handleSubmit(onSubmit) }}
            isLoading={editNotation.isPending}
            {...form}
          />
          <Highlights
            highlight={highlightColor}
            setHighlight={setHighlightColor}
            variant="edit"
            css={{ padding: spacing.none }}
            onMouseDown={(e) => e.preventDefault()}
          />
        </FullscreenModalModal>
      );
    },
  ),
  View: createModal<BibleNoteModalProps>(
    ({ note, color, text, title, notationId, start, end, ...props }) => {
      const modal = useModal();
      const t = useTranslations();
      const editModal = useModal(BibleNoteModal.Edit);

      const switchToEdit = async () => {
        modal.hide();
        await editModal.show({
          text,
          title,
          color,
          note,
          notationId,
          start,
          end,
          ...props,
        });
        modal.resolve();
        modal.remove();
      };

      return (
        <FullscreenModalModal
          open={modal.visible}
          onOpenChange={(open) => {
            if (open) return modal.show();
            modal.resolve();
            modal.remove();
          }}
        >
          <ModalHeader css={header}>{t("bible_note_header")}</ModalHeader>
          <Blockquote>
            <Text lineClamp={5} type="bible">
              <span css={highlight({ highlightId: color })}>{text}</span>
            </Text>
            <Text type="title">{title}</Text>
          </Blockquote>
          <Text size="l">{note}</Text>
          <Button variant="outline" onClick={switchToEdit} css={buttonPlacer}>
            {t("general_word_edit")}
          </Button>
        </FullscreenModalModal>
      );
    },
  ),
};

BibleNoteModal.Create.displayName = "BibleNoteModal.Create";
BibleNoteModal.Edit.displayName = "BibleNoteModal.Edit";
BibleNoteModal.View.displayName = "BibleNoteModal.View";
